import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export interface CustomerDetails {
  id?: string;
  msisdn: string;
  opco: string;
  accessToken: string;
  language?: string;
}

export interface AuthContextValue {
  isLoggedIn: boolean;
  customerDetails: CustomerDetails;
  setMsisdn: (msisdn: string) => void;
  setOpco: (opco: string) => void;
  setLanguage: (language: string) => void;
  setCustomerId: (customerId: string) => void;
  setAccessToken: (accessToken: string) => void;
  invalidCustomer: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{
  children: ReactNode;
  initialUserData: CustomerDetails;
}> = ({ children, initialUserData }) => {
  const storedConfigs = localStorage.getItem('configs');
  const [msisdn, setMsisdn] = useState<string>(initialUserData.msisdn);
  const [languageTag, setLanguageTag] = useState<string>(initialUserData.language ?? '');
  const [opco, setOpco] = useState<string>(initialUserData.opco);
  const [customerId, setCustomerId] = useState<string>(initialUserData.id ?? '');
  const [accessToken, setAccessToken] = useState<string>(
    process.env.SATURN_BFF_TOKEN ?? initialUserData.accessToken
  );
  const [invalidCustomer, setInvalidCustomer] = useState<boolean>(true);

  useEffect(() => {
    if (msisdn === '' && storedConfigs) {
      setMsisdn(JSON.parse(storedConfigs).msisdn);
    }
  }, [msisdn, storedConfigs]);

  useEffect(() => {
    // Load initial token
    if (accessToken === '' && storedConfigs) {
      setAccessToken(JSON.parse(storedConfigs).accessToken);
    }

    // Also update if localStorage has a different token
    if (storedConfigs) {
      const configToken = JSON.parse(storedConfigs).accessToken;
      if (configToken && configToken !== accessToken) {
        setAccessToken(configToken);
      }
    }
  }, [accessToken, storedConfigs]);

  useEffect(() => {
    if (customerId) {
      setInvalidCustomer(false);
    }
  }, [customerId]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: !!msisdn,
        customerDetails: { id: customerId, msisdn, opco, language: languageTag, accessToken },
        setMsisdn,
        setOpco,
        setLanguage: setLanguageTag,
        setCustomerId,
        setAccessToken,
        invalidCustomer,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
